/**
 * Globo Related Products enhancer — idempotent, non-destructive.
 *
 * What it does:
 *   1. Tags Tipo's carousel prev/next buttons with .grp-arrow + direction
 *      (using several selector heuristics so we don't have to know whether
 *      Tipo's build is Owl, Globo's fork, or something else).
 *   2. Injects a .grp-cta element into each .ga-product card so the hover
 *      "View Product" slide-up overlay has something to show.
 *
 * Safety:
 *   - Never removes Tipo elements; only adds classes / .grp-cta spans
 *   - Idempotent (class checks before adding)
 *   - MutationObserver + 500ms poll for 30s to catch late renders
 */
(function () {
  'use strict';

  var ROOT_SEL = '.grp-embed';
  var CTA_TEXT = 'View Product';

  function injectCta(card) {
    var img = card.querySelector('.ga-product_image');
    if (!img) return;
    if (img.querySelector('.grp-cta')) return;
    var span = document.createElement('span');
    span.className = 'grp-cta';
    span.textContent = CTA_TEXT;
    span.setAttribute('aria-hidden', 'true');
    img.appendChild(span);
  }

  function tagArrows(root) {
    /* Try the most common Owl/Globo arrow selectors first, then fall back
       to heuristics on class name or aria-label. */
    var prev = root.querySelector('.grp-arrow--prev');
    var next = root.querySelector('.grp-arrow--next');
    if (prev && next) return;

    if (!prev) {
      prev = root.querySelector(
        '.owl-prev, button.owl-prev, .gowl-prev, .ga-prev, ' +
        '.ga-carousel-prev, [aria-label="Previous"], [aria-label="previous"]'
      );
    }
    if (!next) {
      next = root.querySelector(
        '.owl-next, button.owl-next, .gowl-next, .ga-next, ' +
        '.ga-carousel-next, [aria-label="Next"], [aria-label="next"]'
      );
    }

    /* Last-ditch: scan all buttons / role=button inside the widget
       and match anything whose class looks prev-ish or next-ish. */
    if (!prev || !next) {
      var all = root.querySelectorAll('button, [role="button"]');
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        var cls = ((el.className || '') + '').toLowerCase();
        if (!prev && /\b(prev|previous|left|arrow-l)\b/.test(cls)) prev = el;
        if (!next && /\b(next|right|arrow-r)\b/.test(cls))        next = el;
        if (prev && next) break;
      }
    }

    if (prev) { prev.classList.add('grp-arrow', 'grp-arrow--prev'); fixArrow(prev, 'Previous products'); }
    if (next) { next.classList.add('grp-arrow', 'grp-arrow--next'); fixArrow(next, 'Next products'); }
  }

  /* Discernible text of an element, ignoring aria-hidden descendants
     (e.g. the injected .grp-cta overlay) — mirrors how AT computes a name. */
  function visibleText(el) {
    var clone = el.cloneNode(true);
    var hidden = clone.querySelectorAll('[aria-hidden="true"]');
    for (var i = 0; i < hidden.length; i++) hidden[i].remove();
    return (clone.textContent || '').trim();
  }

  /* Make Tipo/Globo carousel arrows valid for the accessibility tree:
     the app ships them as <button role="presentation"> with no text, which
     axe flags as (a) a button with no discernible name and (b) a focusable
     element wrongly marked presentational. Drop the bogus role and label it. */
  function fixArrow(el, label) {
    if (!el) return;
    var role = el.getAttribute('role');
    if (role === 'presentation' || role === 'none') el.removeAttribute('role');
    el.removeAttribute('aria-hidden');
    if (el.tagName === 'BUTTON' && !el.getAttribute('type')) el.setAttribute('type', 'button');
    if (!el.getAttribute('aria-label') && !visibleText(el)) el.setAttribute('aria-label', label);
  }

  /* Tipo product cards link an image (often alt-less) plus a "View Product"
     overlay we mark aria-hidden, so the <a> ends up with no accessible name.
     Derive a label from the card's product title. */
  function cardTitle(card) {
    var t = card.querySelector(
      '.ga-product_name, .ga-product-name, .ga-title, .ga-product_title, ' +
      '[class*="product_name"], [class*="product-name"]'
    );
    if (t && t.textContent.trim()) return t.textContent.trim();
    var img = card.querySelector('img[alt]');
    if (img && img.getAttribute('alt').trim()) return img.getAttribute('alt').trim();
    return 'View product';
  }

  function labelLinks(card) {
    var links = card.querySelectorAll('a[href]');
    var title = null;
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.getAttribute('aria-label') || visibleText(a)) continue;
      if (title === null) title = cardTitle(card);
      a.setAttribute('aria-label', title);
    }
  }

  /* Fallback for environments where :hover doesn't fire reliably on cards
     that are moved around by the carousel — we mirror hover into a
     data attribute so CSS can target it with full confidence. */
  function bindHover(card) {
    if (card.__grpHoverBound) return;
    card.__grpHoverBound = true;
    card.addEventListener('mouseenter', function () {
      card.setAttribute('data-grp-hover', 'true');
    });
    card.addEventListener('mouseleave', function () {
      card.removeAttribute('data-grp-hover');
    });
  }

  function run() {
    var roots = document.querySelectorAll(ROOT_SEL);
    roots.forEach(function (root) {
      tagArrows(root);
      var cards = root.querySelectorAll('.ga-product');
      cards.forEach(function (card) {
        injectCta(card);
        bindHover(card);
        labelLinks(card);
      });
    });
  }

  function init() {
    var roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return;

    run();

    var observer = new MutationObserver(function () { run(); });
    roots.forEach(function (r) {
      observer.observe(r, { childList: true, subtree: true });
    });

    /* Safety net — poll every 500ms for 30s after load in case
       Tipo's render or re-render lands outside the observer window. */
    var started = Date.now();
    var interval = setInterval(function () {
      run();
      if (Date.now() - started > 30000) clearInterval(interval);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
